import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminHeaders } from '../utils/adminHeaders';
import {
    Typography,
    Input,
    Button,
    Card,
    CardContent,
    Sheet,
    Divider,
    Grid,
    Box,
    Stack,
    Alert,
} from '@mui/joy';
import {
    Search,
    Person,
    DeviceHub,
    Email,
    Memory,
} from '@mui/icons-material';


const SearchPage = () => {
    const { session } = useAuth();
    const [emailQuery, setEmailQuery] = useState('');
    const [hwIdQuery, setHwIdQuery] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [deviceResults, setDeviceResults] = useState([]);
    const [loading, setLoading] = useState({ users: false, devices: false });
    const [message, setMessage] = useState('');
    const navigate = useNavigate();

    const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

    const searchUsers = async () => {
        if (!emailQuery.trim()) {
            setMessage('Please enter an email to search');
            return;
        }

        try {
            setLoading(prev => ({ ...prev, users: true }));
            setMessage('');
            
            const res = await fetch(`${API_BASE}/search/client?email=${encodeURIComponent(emailQuery)}`, {
                headers: adminHeaders(session),
            });
            if (!res.ok) throw new Error('Search failed');
            
            const users = await res.json();
            setUserResults(users);
            setDeviceResults([]);
            setMessage(users.length ? '' : 'No clients found');
        } catch (error) {
            console.error('Search error:', error);
            setMessage('Failed to search clients');
            setUserResults([]);
        } finally {
            setLoading(prev => ({ ...prev, users: false }));
        }
    };

    const searchDevices = async () => {
        if (!hwIdQuery.trim()) {
            setMessage('Please enter a hardware ID to search');
            return;
        }

        try {
            setLoading(prev => ({ ...prev, devices: true }));
            setMessage('');
            
            const res = await fetch(`${API_BASE}/search/device?hardware_id=${encodeURIComponent(hwIdQuery)}`, {
                headers: adminHeaders(session),
            });
            if (!res.ok) throw new Error('Search failed');
            
            const devices = await res.json();
            setDeviceResults(devices);
            setUserResults([]);
            setMessage(devices.length ? '' : 'No devices found');
        } catch (error) {
            console.error('Search error:', error);
            setMessage('Failed to search devices');
            setDeviceResults([]);
        } finally {
            setLoading(prev => ({ ...prev, devices: false }));
        }
    };

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Search sx={{ fontSize: 32, color: 'primary.500' }} />
                    <Box>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            Search & Browse
                        </Typography>
                        <Typography level="body-lg" textColor="neutral.600">
                            Quick search across clients and devices
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Search Section */}
            <Card variant="outlined" sx={{ mb: 4 }}>
                <CardContent>
                    <Typography level="h4" sx={{ mb: 3 }}>
                        Quick Search
            </Typography>

                    <Grid container spacing={3}>
                <Grid xs={12} md={6}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                                Search Clients by Email
                            </Typography>
                    <Input
                                placeholder="Enter client email address"
                        value={emailQuery}
                        onChange={(e) => setEmailQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchUsers()}
                                startDecorator={<Email />}
                        endDecorator={
                                    <Button 
                                        onClick={searchUsers} 
                                        variant="solid"
                                        color="success"
                                        loading={loading.users}
                                        disabled={!emailQuery.trim()}
                                    >
                                Search
                            </Button>
                        }
                                size="lg"
                    />
                </Grid>
                <Grid xs={12} md={6}>
                            <Typography level="body-sm" sx={{ mb: 1 }}>
                                Search Devices by Hardware ID
                            </Typography>
                    <Input
                                placeholder="Enter device hardware ID"
                        value={hwIdQuery}
                        onChange={(e) => setHwIdQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchDevices()}
                                startDecorator={<Memory />}
                        endDecorator={
                                    <Button 
                                        onClick={searchDevices} 
                                        variant="solid"
                                        color="warning"
                                        loading={loading.devices}
                                        disabled={!hwIdQuery.trim()}
                                    >
                                Search
                            </Button>
                        }
                                size="lg"
                    />
                </Grid>
            </Grid>

                    {/* Quick Links */}
                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography level="body-sm" sx={{ mb: 2 }} textColor="neutral.600">
                            Or go to dedicated management pages:
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="outlined"
                                color="success"
                                startDecorator={<Person />}
                                onClick={() => navigate('/admin/clients')}
                            >
                                Client Management
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                startDecorator={<DeviceHub />}
                                onClick={() => navigate('/admin/devices')}
                            >
                                Device Management
                            </Button>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            {/* Messages */}
            {message && (
                <Alert 
                    color={userResults.length > 0 || deviceResults.length > 0 ? "success" : "warning"} 
                    sx={{ mb: 3 }}
                >
                    {message}
                </Alert>
            )}

            {userResults.length > 0 && (
                <>
                    <Typography level="h4" mt={3} mb={1}>
                        Client Results
                    </Typography>
                    <Grid container spacing={2}>
                        {userResults.map((user) => (
                            <Grid key={user.id} xs={12} md={6} lg={4}>
                                <Card 
                                    onClick={() => navigate(`/client/${user.id}`)} 
                                    variant="outlined" 
                                    sx={{ 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 'md',
                                        },
                                    }}
                                >
                                <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'success.softBg',
                                                    color: 'success.500',
                                                }}
                                            >
                                                <Person sx={{ fontSize: 20 }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography level="title-md" sx={{ mb: 0.5 }}>
                                                    {user.name}
                                                </Typography>
                                        <Typography level="body-sm" textColor="neutral.500">
                                            {user.email}
                                        </Typography>
                                            </Box>
                                        </Stack>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography level="body-xs">Log level: {user.log_level}</Typography>
                                        <Typography level="body-xs">ID: {user.id}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}

            {deviceResults.length > 0 && (
                <>
                    <Typography level="h4" mt={3} mb={1}>
                        Device Results
                    </Typography>
                    <Grid container spacing={2}>
                        {deviceResults.map((device) => (
                            <Grid key={device.id} xs={12} md={6} lg={4}>
                                <Card 
                                    onClick={() => navigate(`/hub/${device.id}`)}
                                    variant="outlined"
                                    sx={{ 
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: 'md',
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                                            <Box
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: '50%',
                                                    backgroundColor: 'warning.softBg',
                                                    color: 'warning.500',
                                                }}
                                            >
                                                <DeviceHub sx={{ fontSize: 20 }} />
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography level="title-md" sx={{ mb: 0.5 }}>
                                                    {device.name || `Device ${device.id}`}
                                                </Typography>
                                        <Typography level="body-sm" textColor="neutral.500">
                                            HW ID: {device.hardware_id}
                                        </Typography>
                                            </Box>
                                        </Stack>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography level="body-xs">Owner ID: {device.owner_id}</Typography>
                                        <Typography level="body-xs">Log level: {device.log_level}</Typography>
                                        <Typography level="body-xs">
                                            Created: {new Date(device.created_at).toLocaleString()}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </>
            )}
        </Sheet>
    );
};

export default SearchPage;
